// src/components/StudentEntry.js
import React, { useState, useRef, useEffect } from 'react';
import { Button, Box, Typography, TextField, Paper, Alert, CircularProgress, Avatar } from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';

// 辅助函数：将 File 对象读取为 Base64 Data URL (保持不变)
const readFileAsDataURL = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

// onStudentAdd 现在是一个 async 函数，会返回 true/false
function StudentEntry({ onStudentAdd }) {
  const [name, setName] = useState('');
  const [studentId, setStudentId] = useState('');
  const [faceImageFile, setFaceImageFile] = useState(null); // File 对象
  const [previewImageUrl, setPreviewImageUrl] = useState(null); // 本地预览 Blob URL
  const [isSubmitting, setIsSubmitting] = useState(false); // 提交状态
  const [error, setError] = useState(''); // 表单级别的错误 (例如未填项)
  // 不需要 successMessage 了，成功消息由 App.js 显示

  const imageInputRef = useRef(null);

  // 清理 Blob URL (保持不变)
  useEffect(() => {
    const currentPreviewUrl = previewImageUrl;
    return () => {
      if (currentPreviewUrl && currentPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(currentPreviewUrl);
        // console.log("Revoked preview URL:", currentPreviewUrl);
      }
    };
  }, [previewImageUrl]);

  const handleNameChange = (event) => setName(event.target.value);
  const handleIdChange = (event) => setStudentId(event.target.value);

  // 图片选择处理 (保持不变)
  const handleImageChange = (event) => {
    const file = event.target.files[0];
    setFaceImageFile(null);
    setPreviewImageUrl(prevUrl => {
        if(prevUrl && prevUrl.startsWith('blob:')) URL.revokeObjectURL(prevUrl);
        return null;
    })
    setError(''); // 清除旧错误

    if (file && file.type.startsWith('image/')) {
      setFaceImageFile(file);
      try{
          const url = URL.createObjectURL(file);
          setPreviewImageUrl(url);
      } catch(e) {
          console.error("创建图片预览 URL 时出错:", e);
          setError("无法创建图片预览。");
          setFaceImageFile(null);
      }
    } else {
        if(file) setError('请选择一个有效的图片文件 (如 JPEG, PNG)。');
        else setError(''); // 取消选择时清除错误
        setFaceImageFile(null);
    }
     if (imageInputRef.current) {
        imageInputRef.current.value = '';
     }
  };

  // 表单提交处理
  const handleSubmit = async (event) => {
    event.preventDefault();
    // 检查必填项
    if (!name || !studentId || !faceImageFile) {
      setError('请填写学生姓名、学号并上传人脸图片。');
      return;
    }

    setIsSubmitting(true);
    setError(''); // 清除之前的表单错误
    console.log('StudentEntry: 开始处理学生信息提交:', { name, studentId, imageName: faceImageFile.name });

    try {
      // 将 File 对象转换为 Base64 (保持不变)
      console.log('正在将图片文件转换为 Base64...');
      const faceImageBase64 = await readFileAsDataURL(faceImageFile);
      console.log('图片已转换为 Base64');

      // 准备传递给 App 的数据 (保持不变)
      const newStudentData = {
          name: name,
          studentId: studentId,
          faceImageUrl: faceImageBase64 // Base64 字符串
      };

      // --- 调用从 App 传递过来的 onStudentAdd (现在是 async) ---
      // onStudentAdd 会处理 API 调用并返回 true (成功) 或 false (失败)
      const success = await onStudentAdd(newStudentData);

      if (success) {
        console.log('StudentEntry: 学生信息已成功添加到后端，清空表单。');
        // 成功添加后清空表单
        setName('');
        setStudentId('');
        setFaceImageFile(null);
        setPreviewImageUrl(prevUrl => {
           if(prevUrl && prevUrl.startsWith('blob:')) URL.revokeObjectURL(prevUrl);
           return null;
        });
        if (imageInputRef.current) {
          imageInputRef.current.value = '';
        }
        // 成功消息由 App.js 显示
      } else {
        // 如果 onStudentAdd 返回 false，说明 API 调用失败
        // 错误消息由 App.js 显示，这里不需要额外设置 error state
        console.log('StudentEntry: 添加学生信息到后端失败 (错误信息应在 App.js 显示)。');
        // 不清空表单，允许用户修正或重试
      }

    } catch (err) {
      // 这个 catch 主要捕获 readFileAsDataURL 的错误
      console.error('StudentEntry: 处理图片或调用 onStudentAdd 时出错:', err);
      setError(`信息处理失败: ${err.message || '请检查图片文件或稍后重试。'}`);
    } finally {
      setIsSubmitting(false); // 结束提交状态
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        录入学生信息
      </Typography>

      {/* 显示表单级别的错误 */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {/* 成功和 API 错误消息由 App.js 显示 */}

      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
        {/* 姓名输入 */}
        <TextField
          margin="normal" required fullWidth id="name-fatigue" label="学生姓名" name="name"
          autoComplete="name" autoFocus value={name} onChange={handleNameChange} disabled={isSubmitting}
        />
        {/* 学号输入 */}
        <TextField
          margin="normal" required fullWidth id="studentId-fatigue" label="学号" name="studentId"
          value={studentId} onChange={handleIdChange} disabled={isSubmitting}
        />
        {/* 图片上传和预览 (保持不变) */}
        <Box sx={{ mt: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
           <input
            type="file" accept="image/*" ref={imageInputRef} onChange={handleImageChange}
            style={{ display: 'none' }} id="face-image-upload-fatigue"
          />
          <Button
            variant="outlined" startIcon={<AddAPhotoIcon />} disabled={isSubmitting}
            component="label" htmlFor="face-image-upload-fatigue"
          >
            上传人脸图片
          </Button>
           {previewImageUrl ? (
             <Avatar
                src={previewImageUrl} // 本地预览 URL
                sx={{ width: 80, height: 80 }}
                variant="rounded"
                alt="人脸预览"
                onError={(e) => { console.error("本地预览头像加载失败", e); setError("无法加载图片预览。");}}
            />
          ) : (
            <Avatar sx={{ width: 80, height: 80, bgcolor: 'grey.300' }} variant="rounded">
                <Typography variant="caption" align="center" sx={{ p: 1}}>图片预览</Typography>
            </Avatar>
          )}
           {faceImageFile && (
             <Typography variant="body2" sx={{ flexGrow: 1, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {faceImageFile.name}
            </Typography>
           )}
        </Box>

        {/* 提交按钮 */}
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          disabled={isSubmitting || !name || !studentId || !faceImageFile} // 按钮禁用条件
          startIcon={isSubmitting ? null : <PersonAddIcon />} // 提交中不显示图标
        >
          {isSubmitting ? <CircularProgress size={24} color="inherit" /> : '确认录入'}
        </Button>
        <Alert severity="info" sx={{ mt: 1 }}>
            录入学生的基本信息和人脸照片。提交后信息将发送到后端服务器保存。
        </Alert>
      </Box>
    </Paper>
  );
}

export default StudentEntry;